import cn from 'classnames';
import {
  CSSProperties,
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { useBlocker, useNavigate, useSearchParams } from 'react-router';

import api from '@/shared/api/api';
import {
  AddPatientDocumentImageReq,
  CreatePatientDocumentReq,
  DeletePatientDocumentReq,
  ListPatientDocumentSourceAssetsReq,
  ListPatientDocumentsReq,
  PatientDocumentSourceAsset,
  RemovePatientDocumentImageReq,
  UpdatePatientDocumentImageReq,
  UpdatePatientDocumentReq,
} from '@/shared/api/protocol-ts/api/core/svc_patient_document_pb';
import {
  CloseSessionReq,
  NotifyFileUploadFinishedReq,
  NotifyFileUploadStartedReq,
  StartUploadSessionReq,
} from '@/shared/api/protocol-ts/api/core/svc_upload_pb';
import { CBCT_SectionOrientation } from '@/shared/api/protocol-ts/model/dto_asset_common_pb';
import { Asset, AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { MedicalImageViewOptions } from '@/shared/api/protocol-ts/model/dto_common_image_view_options_pb';
import { PatientDocument } from '@/shared/api/protocol-ts/model/dto_patient_document_pb';
import {
  useAppDispatch,
  useAppSelector,
  useUserTimezone,
} from '@/shared/hooks';
import { getImageSrc, stripChatQuery } from '@/shared/lib';
import { Button, Skeleton, WidgetCard } from '@/shared/ui';

import { assetsModel } from '@/entities/assets';
import { TranscriptorDetailsPopover } from '@/entities/transcriptor';

import { AgentChatHost } from '../AgentChat';

import { patientDocumentMessages } from './config';
import { DocumentImageDisplay } from './DocumentImageDisplay';
import { isDicomSliceAsset } from './lib';
import { imageViewFilter, imageViewTransform } from './lib';
import { MarkdownWysiwygEditor } from './MarkdownWysiwygEditor';
import styles from './PatientDocuments.module.scss';

type Props = {
  patientID: string;
  canEdit: boolean;
  documentID?: string;
  agentDocumentID?: string;
  refreshToken?: number;
  onAgentDocumentConsumed?: () => void;
  onTitleChange?: (title: string) => void;
};

const emptyDraft = {
  title: '',
  body: '',
};

const reportImageTypeLabel = (
  type: AssetType,
  formatMessage: IntlShape['formatMessage'],
) => {
  switch (type) {
    case AssetType.AssetType_Report_Generated_CBCT_ToothPathologySlice:
      return formatMessage(patientDocumentMessages.pathologySlices);
    case AssetType.AssetType_Report_Generated_CBCT_GP_ToothSlice:
      return formatMessage(patientDocumentMessages.toothSlices);
    case AssetType.AssetType_Report_Generated_CBCT_GP_MPRSubVolume:
      return formatMessage(patientDocumentMessages.mprVolumes);
    case AssetType.AssetType_Report_Uploaded_MPRPlane:
      return formatMessage(patientDocumentMessages.mprPlanes);
    case AssetType.AssetType_Report_Uploaded_ToothSlice:
      return formatMessage(patientDocumentMessages.uploadedToothSlices);
    case AssetType.AssetType_Report_Generated_CBCT_GP_PanoramaReformatSplit:
      return formatMessage(patientDocumentMessages.panoramicSplitViews);
    case AssetType.AssetType_Report_Generated_CBCT_GP_PanoramaReformatGeneral:
    case AssetType.AssetType_Report_Generated_Studio_PanoramaReformatGeneral:
      return formatMessage(patientDocumentMessages.panoramicReconstructions);
    case AssetType.AssetType_Report_Generated_CBCT_GP_PeriodontiumImage:
      return formatMessage(patientDocumentMessages.periodontium);
    case AssetType.AssetType_Report_Generated_CBCT_Endo_PanoramaSplit:
    case AssetType.AssetType_Report_Generated_CBCT_Endo_Guide:
      return formatMessage(patientDocumentMessages.endodonticViews);
    case AssetType.AssetType_Report_Generated_CBCT_Endo_CrossSection:
      return formatMessage(patientDocumentMessages.endodonticCrossSections);
    case AssetType.AssetType_Report_Generated_CBCT_Implant_Panorama:
      return formatMessage(patientDocumentMessages.implantViews);
    case AssetType.AssetType_Report_Generated_CBCT_Implant_Slice:
      return formatMessage(patientDocumentMessages.implantSlices);
    case AssetType.AssetType_Report_Generated_CBCT_Molar_Panorama:
    case AssetType.AssetType_Report_Generated_CBCT_Molar_Guide:
      return formatMessage(patientDocumentMessages.molarViews);
    case AssetType.AssetType_Report_Generated_CBCT_Molar_CrossSection:
      return formatMessage(patientDocumentMessages.molarCrossSections);
    case AssetType.AssetType_Report_Generated_IOXRayImage:
      return formatMessage(patientDocumentMessages.intraoralXrays);
    case AssetType.AssetType_Report_Rendered_DisplayImage:
      return formatMessage(patientDocumentMessages.reportFigures);
    default:
      return formatMessage(patientDocumentMessages.reportImages);
  }
};

type ReportImageDetails = {
  label: string;
  orientation?: string;
  order?: number;
  tooth?: number;
  total?: number;
};

const orientationLabel = (
  orientation: CBCT_SectionOrientation,
  formatMessage: IntlShape['formatMessage'],
) => {
  switch (orientation) {
    case CBCT_SectionOrientation.SectionOrientationAxial:
      return formatMessage(patientDocumentMessages.axial);
    case CBCT_SectionOrientation.SectionOrientationVestibuloOral:
      return formatMessage(patientDocumentMessages.coronal);
    case CBCT_SectionOrientation.SectionOrientationMesioDistal:
      return formatMessage(patientDocumentMessages.sagittal);
    default:
      return undefined;
  }
};

const reportImageDetails = (
  asset: Asset,
  formatMessage: IntlShape['formatMessage'],
): ReportImageDetails => {
  const fallback = { label: reportImageTypeLabel(asset.Type, formatMessage) };
  if (asset.Kind.case !== 'GeneratedReport') {
    return fallback;
  }

  const content = asset.Kind.value.Content;
  if (content.case === 'CBCTGPToothSlice') {
    const metadata = content.value.Metadata;
    const regular = metadata.case === 'Regular' ? metadata.value : undefined;
    return {
      label: reportImageTypeLabel(asset.Type, formatMessage),
      tooth: content.value.Numeration?.ISO || undefined,
      orientation: orientationLabel(content.value.Orientation, formatMessage),
      order: regular ? regular.Order + 1 : undefined,
      total: regular?.TotalImages || undefined,
    };
  }
  if (content.case === 'CBCTToothPathologySlice') {
    return {
      label: reportImageTypeLabel(asset.Type, formatMessage),
      tooth: content.value.Numeration?.ISO || undefined,
      orientation: orientationLabel(content.value.Orientation, formatMessage),
    };
  }
  if (content.case === 'CBCTGPPeriodontiumImage') {
    return {
      label: reportImageTypeLabel(asset.Type, formatMessage),
      tooth: content.value.Numeration?.ISO || undefined,
    };
  }
  if (content.case === 'CBCTGPMPRSubVolume') {
    return {
      label: reportImageTypeLabel(asset.Type, formatMessage),
      tooth: content.value.Numeration?.ISO || undefined,
    };
  }
  return fallback;
};

const reportImageDescription = (
  asset: Asset,
  formatMessage: IntlShape['formatMessage'],
) => {
  const details = reportImageDetails(asset, formatMessage);
  return [
    details.label,
    details.tooth
      ? formatMessage(patientDocumentMessages.toothNumber, {
          number: details.tooth,
        })
      : '',
    details.orientation ?? '',
    details.order
      ? formatMessage(
          details.total
            ? patientDocumentMessages.sliceNumberOf
            : patientDocumentMessages.sliceNumber,
          { number: details.order, total: details.total },
        )
      : '',
  ]
    .filter(Boolean)
    .join(' · ');
};

// Card date: creation time, matching the report-card convention (a card
// answers "when did this appear", not "when was it last edited" — the list
// order remains update-driven on the server).
const documentCreatedAt = (document: PatientDocument) =>
  document.Created?.At?.toDate() ??
  document.Revision?.UpdatedAt?.toDate() ??
  undefined;

const sanitizeDocumentMarkdown = (markdown: string) =>
  markdown.replace(/\n{3,}/g, '\n\n').trim();

const withoutRedundantTitle = (markdown: string, title: string) => {
  const normalizedTitle = title.trim().toLocaleLowerCase();
  const lines = sanitizeDocumentMarkdown(markdown).split('\n');
  if (
    lines[0]
      ?.replace(/^#{1,6}\s+/, '')
      .trim()
      .toLocaleLowerCase() === normalizedTitle
  ) {
    return lines.slice(1).join('\n').trim();
  }
  return lines.join('\n').trim();
};

const imageReferencePattern = /!\[([^\]]*)]\(image:(\d+)\)/g;

/** Serial numbers referenced by the markdown image references. */
const getImageReferenceSerials = (markdown: string) =>
  Array.from(markdown.matchAll(imageReferencePattern)).map((match) =>
    Number(match[2]),
  );

const resolveAssetSrc = (assetID?: string) =>
  assetID ? getImageSrc(assetID, 'preview') : '';

// Document markdown is authored by other users and produced by the agent from
// patient data, so a link target is untrusted. Allow only http(s)/mailto/tel
// and relative URLs; anything else (notably javascript:) is dropped so it
// cannot execute when clicked.
const isSafeLinkHref = (href: string) => {
  const trimmed = href.trim();
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) {
    return true;
  }
  return !/^[a-z][a-z0-9+.-]*:/i.test(trimmed);
};

/** Custom renderer override for a markdown image reference. */
type RenderImage = (options: {
  destination: string;
  alt: string;
  style?: CSSProperties;
}) => ReactNode;

const PreviewImage: FC<{
  source: string;
  alt: string;
  style?: CSSProperties;
}> = ({ source, alt, style }) => {
  const { formatMessage } = useIntl();
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [source]);

  if (failed) {
    return (
      <span
        className={styles.previewImageUnavailable}
        role="img"
        aria-label={alt}
      >
        {formatMessage(patientDocumentMessages.imageUnavailable)}
      </span>
    );
  }

  return (
    <img
      src={source}
      alt={alt}
      loading="lazy"
      style={style}
      onError={() => setFailed(true)}
    />
  );
};

const renderInlineMarkdown = (
  text: string,
  resolveAsset?: (source: string) => string | undefined,
  styleFor?: (source: string) => CSSProperties | undefined,
  renderImage?: RenderImage,
) => {
  const parts = text.split(
    /(!\[[^\]]*]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+]\([^)]+\))/g,
  );

  return parts.map((part, index) => {
    const image = part.match(/^!\[([^\]]*)]\(([^)]+)\)$/);
    if (image) {
      const style = styleFor?.(image[2]);
      const custom = renderImage?.({
        destination: image[2],
        alt: image[1],
        style,
      });
      if (custom != null) {
        return <Fragment key={`img-${index}`}>{custom}</Fragment>;
      }
      const source = resolveAsset?.(image[2]);
      if (!source) {
        return null;
      }
      return (
        <PreviewImage
          key={`img-${index}`}
          source={source}
          alt={image[1]}
          style={style}
        />
      );
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (
      (part.startsWith('*') && part.endsWith('*')) ||
      (part.startsWith('_') && part.endsWith('_'))
    ) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={index}>{part.slice(1, -1)}</code>;
    }
    const link = part.match(/^\[([^\]]+)]\(([^)]+)\)$/);
    if (link) {
      if (!isSafeLinkHref(link[2])) {
        return link[1];
      }
      return (
        <a
          key={index}
          href={stripChatQuery(link[2], window.location.href)}
          target="_blank"
          rel="noreferrer"
        >
          {link[1]}
        </a>
      );
    }
    return part;
  });
};

const parseTableRow = (line: string) => {
  const source = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let cell = '';
  let escaped = false;
  let inCode = false;

  for (const character of source) {
    if (escaped) {
      cell += character;
      escaped = false;
    } else if (character === '\\') {
      escaped = true;
    } else if (character === '`') {
      inCode = !inCode;
      cell += character;
    } else if (character === '|' && !inCode) {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += character;
    }
  }
  cells.push(cell.trim());

  return cells;
};

const isTableSeparator = (line: string) => {
  const cells = parseTableRow(line);
  return cells.length > 1 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

const getTableAlignment = (separator: string) => {
  if (separator.startsWith(':') && separator.endsWith(':')) {
    return 'center';
  }
  if (separator.endsWith(':')) {
    return 'right';
  }
  return 'left';
};

// Renders a paragraph that consists only of image references as block-level
// <figure>s, so a caption lives in a valid <figcaption> instead of inside <p>.
const renderBlockImages = (
  key: number,
  text: string,
  resolveAsset?: (source: string) => string | undefined,
  captionFor?: (source: string) => string | undefined,
  styleFor?: (source: string) => CSSProperties | undefined,
  renderImage?: RenderImage,
): JSX.Element => {
  const tokens = text.match(/![^\]]*\]\([^)]+\)/g) ?? [];
  return (
    <div key={key} className={styles.previewRow}>
      {tokens.map((token, index) => {
        const alt = token.match(/^!\[([^\]]*)\]/)?.[1] ?? 'image';
        const destination = token.match(/\]\(([^)]+)\)/)?.[1] ?? '';
        const style = styleFor?.(destination);
        const resolvedSource = resolveAsset?.(destination);
        const custom = renderImage?.({
          destination,
          alt,
          style,
        });
        if (custom == null && !resolvedSource) {
          return null;
        }
        const preview = custom ?? (
          <PreviewImage
            key={`img-${index}`}
            source={resolvedSource as string}
            alt={alt}
            style={style}
          />
        );
        const caption = captionFor?.(destination);
        return caption ? (
          <figure key={index} className={styles.previewFigure}>
            {preview}
            <figcaption>{caption}</figcaption>
          </figure>
        ) : (
          preview
        );
      })}
    </div>
  );
};

export const MarkdownPreview: FC<{
  markdown: string;
  resolveAsset?: (source: string) => string | undefined;
  captionFor?: (source: string) => string | undefined;
  styleFor?: (source: string) => CSSProperties | undefined;
  renderImage?: RenderImage;
}> = ({ markdown, resolveAsset, captionFor, styleFor, renderImage }) => {
  const blocks: JSX.Element[] = [];
  const lines = markdown.trim().split('\n');

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();
    if (!line) {
      continue;
    }

    if (
      line.includes('|') &&
      index + 1 < lines.length &&
      isTableSeparator(lines[index + 1])
    ) {
      const headers = parseTableRow(line);
      const separators = parseTableRow(lines[index + 1]);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && lines[index].trim().includes('|')) {
        rows.push(parseTableRow(lines[index]));
        index += 1;
      }
      index -= 1;
      blocks.push(
        <div className={styles.tableScroll} key={`table-${index}`}>
          <table>
            <thead>
              <tr>
                {headers.map((header, cellIndex) => (
                  <th
                    key={cellIndex}
                    scope="col"
                    style={{
                      textAlign: getTableAlignment(
                        separators[cellIndex] || '---',
                      ),
                    }}
                  >
                    {renderInlineMarkdown(header, resolveAsset, styleFor)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {headers.map((_, cellIndex) => (
                    <td
                      key={cellIndex}
                      style={{
                        textAlign: getTableAlignment(
                          separators[cellIndex] || '---',
                        ),
                      }}
                    >
                      {renderInlineMarkdown(
                        row[cellIndex] || '',
                        resolveAsset,
                        styleFor,
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (/^[-*] /.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^[-*] /.test(lines[index].trim())) {
        items.push(lines[index].trim().slice(2));
        index += 1;
      }
      index -= 1;
      blocks.push(
        <ul key={`ul-${index}`}>
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>
              {renderInlineMarkdown(item, resolveAsset, styleFor, renderImage)}
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\. /.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^\d+\.\s+/, ''));
        index += 1;
      }
      index -= 1;
      blocks.push(
        <ol key={`ol-${index}`}>
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>
              {renderInlineMarkdown(item, resolveAsset, styleFor, renderImage)}
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const HeadingTag = `h${Math.min(heading[1].length + 1, 6)}` as keyof Pick<
        JSX.IntrinsicElements,
        'h2' | 'h3' | 'h4' | 'h5' | 'h6'
      >;
      blocks.push(
        <HeadingTag key={index}>
          {renderInlineMarkdown(heading[2], resolveAsset, styleFor)}
        </HeadingTag>,
      );
    } else {
      const paragraph = [line];
      while (
        index + 1 < lines.length &&
        lines[index + 1].trim() &&
        !/^(#{1,6}\s|[-*]\s|\d+\.\s)/.test(lines[index + 1].trim())
      ) {
        paragraph.push(lines[index + 1].trim());
        index += 1;
      }
      const paragraphText = paragraph.join(' ');
      const imagesOnly = /^(!\[[^\]]*]\([^)]+\)\s*)+$/.test(paragraphText);
      blocks.push(
        imagesOnly ? (
          renderBlockImages(
            index,
            paragraphText,
            resolveAsset,
            captionFor,
            styleFor,
            renderImage,
          )
        ) : (
          <p key={index}>
            {renderInlineMarkdown(paragraphText, resolveAsset, styleFor)}
          </p>
        ),
      );
    }
  }

  return <>{blocks}</>;
};

/** Standalone patient-page tile for one document, styled like a consultation card. */
const PatientDocumentTileCard: FC<{
  document: PatientDocument;
  onClick: () => void;
  onDelete?: () => void;
}> = ({ document, onClick, onDelete }) => {
  const { formatDate } = useIntl();
  const userTimezone = useUserTimezone();
  const date = documentCreatedAt(document);

  return (
    <div
      data-component="patient-document-card"
      data-id={document.ID}
      className={styles.documentCard}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <header className={styles.documentCardHeader}>
        <h4 className={cn(styles.documentTitle, 'h4')}>
          <span className={styles.documentTitleText}>{document.Title}</span>
          {onDelete && <TranscriptorDetailsPopover onDelete={onDelete} />}
        </h4>
        {date && (
          <p className={cn(styles.documentDate, 'p2')}>
            {formatDate(date, {
              dateStyle: 'medium',
              timeStyle: 'short',
              timeZone: userTimezone,
            })}
          </p>
        )}
      </header>
      {document.Body.trim() && (
        <div className={styles.documentPreview}>
          <MarkdownPreview
            markdown={withoutRedundantTitle(document.Body, document.Title)}
          />
        </div>
      )}
    </div>
  );
};

export const PatientDocuments: FC<Props> = ({
  patientID,
  documentID,
  canEdit,
  agentDocumentID,
  refreshToken = 0,
  onAgentDocumentConsumed,
  onTitleChange,
}) => {
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const draftStorageKey = `patient-document-draft:${patientID}`;
  const editingStorageKey = `patient-document-editing:${patientID}`;
  const assetPickerStorageKey = `patient-document-asset-picker:${patientID}`;
  const selectedStorageKey = `patient-document-selected:${patientID}`;
  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [selected, setSelected] = useState<PatientDocument>();
  const [draft, setDraft] = useState(() => {
    const stored = window.sessionStorage.getItem(draftStorageKey);
    if (!stored) {
      return emptyDraft;
    }
    try {
      const parsed: unknown = JSON.parse(stored);
      if (
        parsed &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed) &&
        typeof (parsed as { title?: unknown }).title === 'string' &&
        typeof (parsed as { body?: unknown }).body === 'string'
      ) {
        return parsed as typeof emptyDraft;
      }
    } catch {
      return emptyDraft;
    }
    return emptyDraft;
  });
  const [editing, setEditing] = useState(
    () => window.sessionStorage.getItem(editingStorageKey) === 'true',
  );
  const [busy, setBusy] = useState(false);
  const [editorMutationPending, setEditorMutationPending] = useState(false);
  const [error, setError] = useState('');
  const [baseSourceAssets, setSourceAssets] = useState<
    PatientDocumentSourceAsset[]
  >([]);
  const [toothSliceAssets, setToothSliceAssets] = useState<
    PatientDocumentSourceAsset[]
  >([]);
  const [sourceAssetsLoading, setSourceAssetsLoading] = useState(false);
  const [sourceReportID, setSourceReportID] = useState('');
  const [visibleSourceAssetCount, setVisibleSourceAssetCount] = useState(12);
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<AssetType>();
  const [sourceToothFilter, setSourceToothFilter] = useState<number>();
  const [sourceOrientationFilter, setSourceOrientationFilter] =
    useState<string>();
  const [failedSourceAssetIDs, setFailedSourceAssetIDs] = useState<Set<string>>(
    () => new Set(),
  );
  const [assetPickerOpen, setAssetPickerOpen] = useState(
    () => window.sessionStorage.getItem(assetPickerStorageKey) === 'true',
  );
  const [selectedDocumentText, setSelectedDocumentText] = useState('');
  const [lightboxAsset, setLightboxAsset] =
    useState<PatientDocumentSourceAsset | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const assetBrowserRef = useRef<HTMLDivElement>(null);
  const insertImageRef = useRef<
    ((serial: number, onInserted?: () => void) => void) | null
  >(null);
  const replaceSelectionRef = useRef<((text: string) => boolean) | null>(null);
  const insertBelowSelectionRef = useRef<((text: string) => boolean) | null>(
    null,
  );
  const savedDraftRef = useRef(JSON.stringify(draft));
  // Set while an implicit document create is in flight: the route effect
  // must adopt the created document instead of re-entering its new branch.
  const implicitNewCreateRef = useRef(false);
  const dispatch = useAppDispatch();
  const documentImageAssetIDs = useMemo(
    () => (selected?.Images.map((image) => image.AssetID) ?? []).join(','),
    [selected],
  );
  const hasAllDocumentImageAssets = useAppSelector(
    (state) =>
      documentImageAssetIDs === '' ||
      (selected?.Images.every((image) =>
        Boolean(assetsModel.selectors.selectById(state, image.AssetID)),
      ) ??
        true),
  );
  // The medical image renderer draws document images from the patient's
  // assets slice. Document DTOs only carry AssetIDs, so backfill the slice
  // with the matching source assets when they are not already loaded.
  const assetsFetchRequestedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!documentImageAssetIDs) {
      return;
    }
    if (hasAllDocumentImageAssets) {
      return;
    }
    const key = `${patientID}:${documentImageAssetIDs}`;
    if (assetsFetchRequestedRef.current.has(key)) {
      return;
    }
    assetsFetchRequestedRef.current.add(key);
    void api.patientDocument
      .listPatientDocumentSourceAssets(
        new ListPatientDocumentSourceAssetsReq({ PatientID: patientID }),
      )
      .then((response) => {
        const assets = response.Assets.map((item) => item.Asset).filter(
          (asset): asset is Asset => Boolean(asset),
        );
        if (assets.length) {
          dispatch(assetsModel.actions.setMany(assets));
        }
      })
      .catch(() => undefined);
  }, [documentImageAssetIDs, hasAllDocumentImageAssets, patientID]);

  const hasUnsavedChanges =
    Boolean(documentID) &&
    editing &&
    JSON.stringify(draft) !== savedDraftRef.current;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return;
    }
    if (window.confirm(formatMessage(patientDocumentMessages.discardChanges))) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [hasUnsavedChanges]);

  const updateWorkspaceUrl = useCallback(
    (updates: Record<string, string | undefined>, replace = false) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      });
      const nextDocumentID =
        'document' in updates
          ? updates.document
          : documentID || next.get('document');
      next.delete('document');
      const pathname = nextDocumentID
        ? `/patients/${patientID}/documents/${encodeURIComponent(nextDocumentID)}`
        : `/patients/${patientID}`;
      navigate({ pathname, search: next.toString() }, { replace });
    },
    [documentID, patientID, searchParams],
  );

  const load = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const response = await api.patientDocument.listPatientDocuments(
        new ListPatientDocumentsReq({ PatientID: patientID }),
      );
      setDocuments(response.Documents);
      setSelected((current) =>
        current
          ? response.Documents.find((item) => item.ID === current.ID)
          : response.Documents.find(
              (item) =>
                item.ID === window.sessionStorage.getItem(selectedStorageKey),
            ),
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setDocumentsLoading(false);
    }
  }, [patientID, selectedStorageKey]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  useEffect(() => {
    if (documentsLoading) {
      return;
    }

    const documentParam = documentID || searchParams.get('document');
    if (!documentID && documentParam) {
      updateWorkspaceUrl({ document: documentParam }, true);
      return;
    }
    const modeParam = searchParams.get('documentMode');

    if (!documentParam) {
      return;
    }

    if (documentParam === 'new') {
      if (!canEdit) {
        setEditing(false);
        setError(formatMessage(patientDocumentMessages.selectedUnavailable));
        return;
      }
      // An implicit create (image inserted with no backing document) just
      // selected its freshly created document: keep the draft and editing
      // state and adopt it — this run of the effect must not reset.
      if (implicitNewCreateRef.current) {
        implicitNewCreateRef.current = false;
        return;
      }
      if (!editing || selected) {
        const nextDraft = {
          ...emptyDraft,
          title: formatMessage(patientDocumentMessages.treatmentPlan),
          body: '',
        };
        setSelected(undefined);
        setSelectedDocumentText('');
        savedDraftRef.current = '';
        setDraft(nextDraft);
        setEditing(true);
      }
      return;
    }

    const document = documents.find((item) => item.ID === documentParam);
    if (!document) {
      setSelected(undefined);
      setEditing(false);
      setError(formatMessage(patientDocumentMessages.selectedUnavailable));
      return;
    }

    if (selected?.ID !== document.ID) {
      setSelected(document);
      setSelectedDocumentText('');
    }

    if (modeParam === 'edit' && canEdit) {
      if (!editing || selected?.ID !== document.ID) {
        const nextDraft = {
          title: document.Title,
          body: withoutRedundantTitle(document.Body, document.Title),
        };
        savedDraftRef.current = JSON.stringify(nextDraft);
        setDraft(nextDraft);
        setEditing(true);
      }
    } else {
      setEditing(false);
    }
  }, [
    canEdit,
    documentID,
    documents,
    documentsLoading,
    editing,
    searchParams,
    selected,
    updateWorkspaceUrl,
  ]);

  const beginEdit = () => {
    if (!selected) {
      return;
    }
    setSelectedDocumentText('');
    const nextDraft = {
      title: selected.Title,
      body: withoutRedundantTitle(selected.Body, selected.Title),
    };
    savedDraftRef.current = JSON.stringify(nextDraft);
    setDraft(nextDraft);
    window.sessionStorage.setItem(draftStorageKey, JSON.stringify(nextDraft));
    setEditing(true);
    window.sessionStorage.setItem(editingStorageKey, 'true');
    setError('');
    updateWorkspaceUrl({
      document: selected.ID,
      documentMode: 'edit',
      documentPane: 'document',
    });
  };

  const updateDraft = (nextDraft: typeof emptyDraft) => {
    setDraft(nextDraft);
    window.sessionStorage.setItem(draftStorageKey, JSON.stringify(nextDraft));
  };

  // Lexical's change plugin fires while the editor initializes and
  // round-trips the markdown through its normalizer, so the string differs
  // from the saved body even with no user edits (and dev StrictMode makes
  // it fire more than once). Until the user actually interacts with the
  // editor, treat every emission as the unsaved-changes baseline:
  // cancelling right after opening the editor must never prompt.
  const editorUserEditedRef = useRef(false);
  useEffect(() => {
    editorUserEditedRef.current = false;
    const surface = document.querySelector(
      '[data-testid="document-editor-surface"]',
    );
    if (!surface) {
      return;
    }
    const markUserEdited = () => {
      editorUserEditedRef.current = true;
    };
    const options = { capture: true };
    surface.addEventListener('keydown', markUserEdited, options);
    surface.addEventListener('beforeinput', markUserEdited, options);
    surface.addEventListener('pointerdown', markUserEdited, options);
    return () => {
      surface.removeEventListener('keydown', markUserEdited, options);
      surface.removeEventListener('beforeinput', markUserEdited, options);
      surface.removeEventListener('pointerdown', markUserEdited, options);
    };
  }, [editing, selected?.ID]);

  const updateDraftFromEditor = (body: string) => {
    if (!editorUserEditedRef.current) {
      setDraft((current) => {
        const baseline = JSON.stringify({ ...current, body });
        savedDraftRef.current = baseline;
        window.sessionStorage.setItem(draftStorageKey, baseline);
        return { ...current, body };
      });
      return;
    }
    updateDraft({ ...draft, body });
  };

  const closeEditor = () => {
    savedDraftRef.current = JSON.stringify(draft);
    setEditing(false);
    window.sessionStorage.removeItem(editingStorageKey);
    window.sessionStorage.removeItem(draftStorageKey);
  };

  const cancelEditing = () => {
    const hasUnsavedChanges = JSON.stringify(draft) !== savedDraftRef.current;
    if (
      hasUnsavedChanges &&
      !window.confirm(formatMessage(patientDocumentMessages.discardChanges))
    ) {
      return;
    }

    closeEditor();
    setError('');
    if (selected) {
      setDraft({
        title: selected.Title,
        body: withoutRedundantTitle(selected.Body, selected.Title),
      });
      updateWorkspaceUrl({
        document: selected.ID,
        documentMode: 'view',
      });
      return;
    }

    updateWorkspaceUrl({
      document: undefined,
      documentMode: undefined,
      documentPane: undefined,
    });
  };

  const openDocument = useCallback(
    (document: PatientDocument) => {
      if (editing && JSON.stringify(draft) !== savedDraftRef.current) {
        if (
          !window.confirm(formatMessage(patientDocumentMessages.discardChanges))
        ) {
          return;
        }
        savedDraftRef.current = JSON.stringify(draft);
      }
      setSelected(document);
      setSelectedDocumentText('');
      setEditing(false);
      setAssetPickerOpen(false);
      window.sessionStorage.setItem(selectedStorageKey, document.ID);
      updateWorkspaceUrl({
        document: document.ID,
        documentMode: 'view',
        documentPane: 'document',
      });
    },
    [draft, editing, selectedStorageKey, updateWorkspaceUrl],
  );

  const deleteDocument = useCallback(
    async (documentID: string) => {
      if (!window.confirm(formatMessage(patientDocumentMessages.remove))) {
        return;
      }
      try {
        await api.patientDocument.deletePatientDocument(
          new DeletePatientDocumentReq({ DocumentID: documentID }),
        );
        if (window.sessionStorage.getItem(selectedStorageKey) === documentID) {
          window.sessionStorage.removeItem(selectedStorageKey);
        }
        await load();
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : String(reason));
      }
    },
    [load, selectedStorageKey],
  );

  useEffect(() => {
    if (!agentDocumentID) {
      return;
    }
    void api.patientDocument
      .listPatientDocuments(
        new ListPatientDocumentsReq({ PatientID: patientID }),
      )
      .then((response) => {
        setDocuments(response.Documents);
        const document = response.Documents.find(
          (item) => item.ID === agentDocumentID,
        );
        if (document) {
          openDocument(document);
        } else {
          setError(
            formatMessage(patientDocumentMessages.createdDocumentUnavailable),
          );
        }
      })
      .catch((reason) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => onAgentDocumentConsumed?.());
  }, [agentDocumentID, onAgentDocumentConsumed, openDocument, patientID]);

  const documentTitle = editing
    ? draft.title || formatMessage(patientDocumentMessages.untitledDocument)
    : selected?.Title || '';
  useEffect(() => {
    onTitleChange?.(documentTitle);
  }, [documentTitle, onTitleChange]);

  const printDocument = async () => {
    const preview = document.querySelector<HTMLElement>(
      '[data-testid="patient-document-preview"]',
    );
    const images = preview ? Array.from(preview.querySelectorAll('img')) : [];
    const pendingImages = images
      .filter((image) => !image.complete)
      .map(
        (image) =>
          new Promise<void>((resolve) => {
            image.addEventListener('load', () => resolve(), { once: true });
            image.addEventListener('error', () => resolve(), { once: true });
          }),
      );

    if (pendingImages.length > 0) {
      await Promise.race([
        Promise.all(pendingImages),
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, 4000);
        }),
      ]);
    }

    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => resolve()),
      );
    });
    window.print();
  };

  // Creates the backing document for an implicit insert (image with no
  // selected document). Returns whether it was freshly created so a failed
  // insert can roll it back instead of leaving an orphan in the list.
  const ensureDocument = async (): Promise<{
    document: PatientDocument;
    created: boolean;
  }> => {
    if (selected) {
      return { document: selected, created: false };
    }
    const createResponse = await api.patientDocument.createPatientDocument(
      new CreatePatientDocumentReq({
        PatientID: patientID,
        Title:
          draft.title || formatMessage(patientDocumentMessages.treatmentPlan),
        Body: draft.body,
      }),
    );
    if (!createResponse.Document) {
      throw new Error(formatMessage(patientDocumentMessages.createFailed));
    }
    // The route effect re-runs when its deps see the new selection and would
    // re-enter the `?document=new` branch — discarding the selection and
    // draft, orphaning this document and duplicating it on the next save.
    // Arm the guard so the effect adopts the created document instead.
    implicitNewCreateRef.current = true;
    setSelected(createResponse.Document);
    return { document: createResponse.Document, created: true };
  };

  const discardImplicitDocument = async (
    implicit: { document: PatientDocument; created: boolean } | null,
  ) => {
    if (!implicit?.created) {
      return;
    }
    try {
      await api.patientDocument.deletePatientDocument(
        new DeletePatientDocumentReq({ DocumentID: implicit.document.ID }),
      );
    } catch {
      // Best effort — the list reload below reconciles the UI.
    }
  };

  const save = async () => {
    if (!draft.title.trim()) {
      setError(formatMessage(patientDocumentMessages.titleRequired));
      return;
    }
    setBusy(true);
    setError('');
    let rollbackDocumentID = '';
    let createdDocument = false;
    // Set once updatePatientDocument resolves: from that point the document
    // (and the user's content) exists server-side and must never be rolled
    // back — a failure in the post-save image-cleanup loop only means the
    // user keeps the saved document and sees the error.
    let savedSuccessfully = false;
    try {
      let workingDocument = selected;
      if (!workingDocument) {
        const createResponse = await api.patientDocument.createPatientDocument(
          new CreatePatientDocumentReq({
            PatientID: patientID,
            Title: draft.title,
            Body: draft.body,
          }),
        );
        workingDocument = createResponse.Document;
        createdDocument = Boolean(workingDocument);
      }
      if (!workingDocument) {
        throw new Error(formatMessage(patientDocumentMessages.saveFailed));
      }
      rollbackDocumentID = workingDocument.ID;
      let currentDocument: PatientDocument = workingDocument;

      const finalMarkdown = sanitizeDocumentMarkdown(draft.body);
      const response = await api.patientDocument.updatePatientDocument(
        new UpdatePatientDocumentReq({
          DocumentID: currentDocument.ID,
          Title: draft.title.trim(),
          Body: finalMarkdown,
          ExpectedRevision: currentDocument.Revision?.Number ?? 0,
        }),
      );
      if (!response.Document) {
        throw new Error(formatMessage(patientDocumentMessages.saveFailed));
      }
      currentDocument = response.Document;
      savedSuccessfully = true;

      // Reconcile the server-owned image list with the markdown: drop any
      // image whose serial is no longer referenced in the saved body.
      const referencedSerials = new Set(
        getImageReferenceSerials(finalMarkdown),
      );
      for (const image of currentDocument.Images) {
        if (referencedSerials.has(image.ID)) {
          continue;
        }
        const removeResponse =
          await api.patientDocument.removePatientDocumentImage(
            new RemovePatientDocumentImageReq({
              DocumentID: currentDocument.ID,
              ImageID: image.ID,
              ExpectedRevision: currentDocument.Revision?.Number ?? 0,
            }),
          );
        if (removeResponse.Document) {
          currentDocument = removeResponse.Document;
        }
      }

      setSelected(currentDocument);
      savedDraftRef.current = JSON.stringify({
        title: currentDocument.Title,
        body: currentDocument.Body,
      });
      window.sessionStorage.setItem(selectedStorageKey, currentDocument.ID);
      closeEditor();
      updateWorkspaceUrl({
        document: currentDocument.ID,
        documentMode: 'view',
      });
      await load();
    } catch (reason) {
      try {
        if (createdDocument && rollbackDocumentID && !savedSuccessfully) {
          await api.patientDocument.deletePatientDocument(
            new DeletePatientDocumentReq({ DocumentID: rollbackDocumentID }),
          );
        }
      } catch {
        // Preserve the original save failure. A reload below reconciles the UI.
      }
      await load();
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  };

  // Patient image uploads finalize asynchronously after the session closes, so poll
  // the source-asset listing until the asset whose original file matches this upload
  // appears. Correlating by file ID (not "any new asset") keeps concurrent uploads
  // from one another's finalized images.
  const findUploadedImageByFileID = async (fileID: string) => {
    const response = await api.patientDocument.listPatientDocumentSourceAssets(
      new ListPatientDocumentSourceAssetsReq({ PatientID: patientID }),
    );
    for (const source of response.Assets) {
      const kind = source.Asset?.Kind;
      if (
        source.Asset?.Type === AssetType.AssetType_Patient_UploadedImage &&
        kind?.case === 'Patient' &&
        kind.value.Content.case === 'UploadedImage' &&
        kind.value.Content.value.Original?.ID === fileID
      ) {
        return source.Asset?.ID ?? '';
      }
    }
    return '';
  };

  // Cancelled on unmount or patient switch: the finalize poll must stop
  // hitting the storage API (and setting state) for a widget that is gone.
  const uploadPollCancelledRef = useRef(false);
  useEffect(() => {
    uploadPollCancelledRef.current = false;
    return () => {
      uploadPollCancelledRef.current = true;
    };
  }, [patientID]);

  const discoverPatientUploadedImage = async (fileID: string) => {
    const deadline = Date.now() + 30000;
    while (Date.now() < deadline && !uploadPollCancelledRef.current) {
      await new Promise((resolve) => window.setTimeout(resolve, 800));
      if (uploadPollCancelledRef.current) {
        return '';
      }
      const assetID = await findUploadedImageByFileID(fileID);
      if (assetID) {
        return assetID;
      }
    }
    return '';
  };

  const stageUpload = async (file: File) => {
    setBusy(true);
    setError('');
    try {
      const startResponse = await api.storage.startUploadSession(
        new StartUploadSessionReq({
          Upload: {
            case: 'PatientUploadedImage',
            value: {
              PatientID: patientID,
              FilePath: file.name,
            },
          },
        }),
      );
      const sessionID = startResponse.UploadSession?.ID ?? '';
      const target = startResponse.UploadTargets?.[0];
      if (!sessionID || !target?.UploadURL || !target.FileID) {
        throw new Error(
          formatMessage(patientDocumentMessages.uploadStartFailed),
        );
      }
      await api.storage.notifyFileUploadStarted(
        new NotifyFileUploadStartedReq({ FileID: target.FileID }),
      );
      const uploadResponse = await fetch(target.UploadURL, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error(formatMessage(patientDocumentMessages.uploadFailed));
      }
      await api.storage.notifyFileUploadFinished(
        new NotifyFileUploadFinishedReq({ FileID: target.FileID }),
      );
      // Patient image uploads finalize asynchronously after the session closes;
      // correlate the finalized asset to this upload by its original file ID.
      await api.storage.closeSession(
        new CloseSessionReq({ UploadSessionID: sessionID }),
      );
      const assetID = await discoverPatientUploadedImage(target.FileID);
      if (!assetID) {
        throw new Error(
          formatMessage(patientDocumentMessages.uploadRegistrationPending),
        );
      }
      let implicitDocument: {
        document: PatientDocument;
        created: boolean;
      } | null = null;
      try {
        implicitDocument = await ensureDocument();
        const document = implicitDocument.document;
        const response = await api.patientDocument.addPatientDocumentImage(
          new AddPatientDocumentImageReq({
            DocumentID: document.ID,
            AssetID: assetID,
            Caption:
              file.name || formatMessage(patientDocumentMessages.uploadedImage),
            ExpectedRevision: document.Revision?.Number ?? 0,
          }),
        );
        if (!response.Document) {
          throw new Error(
            formatMessage(patientDocumentMessages.imageInsertFailed),
          );
        }
        setSelected(response.Document);
        const image =
          response.Document.Images[response.Document.Images.length - 1];
        setEditorMutationPending(true);
        insertImageRef.current?.(image.ID, () =>
          setEditorMutationPending(false),
        );
      } catch (reason) {
        await discardImplicitDocument(implicitDocument);
        await load();
        setError(reason instanceof Error ? reason.message : String(reason));
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
      if (fileInput.current) {
        fileInput.current.value = '';
      }
    }
  };

  const updateImage = async (
    serial: number,
    caption: string,
    viewOptions?: MedicalImageViewOptions,
  ) => {
    const current = selected?.Images.find((image) => image.ID === serial);
    if (!selected || !current) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await api.patientDocument.updatePatientDocumentImage(
        new UpdatePatientDocumentImageReq({
          DocumentID: selected.ID,
          ImageID: serial,
          Caption: caption,
          ViewOptions: viewOptions ?? current.ViewOptions,
          ExpectedRevision: selected.Revision?.Number ?? 0,
        }),
      );
      if (response.Document) {
        setSelected(response.Document);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  };

  const loadSourceAssets = useCallback(async () => {
    setSourceAssetsLoading(true);
    try {
      const response =
        await api.patientDocument.listPatientDocumentSourceAssets(
          new ListPatientDocumentSourceAssetsReq({ PatientID: patientID }),
        );
      const reportImages = response.Assets.filter((source) =>
        Boolean(
          source.Asset &&
            source.Asset.Type !==
              AssetType.AssetType_Report_Generated_CBCT_GP_MPRSubVolume,
        ),
      );
      setSourceAssets(reportImages);
      setFailedSourceAssetIDs(new Set());
      const assets = reportImages
        .map((source) => source.Asset)
        .filter((asset): asset is Asset => Boolean(asset));
      if (assets.length) {
        dispatch(assetsModel.actions.setMany(assets));
      }
      setSourceReportID((current) =>
        reportImages.some((item) => item.ReportID === current)
          ? current
          : reportImages[0]?.ReportID ?? '',
      );
      setVisibleSourceAssetCount(12);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setSourceAssetsLoading(false);
    }
  }, [patientID]);

  const sourceAssets = useMemo(
    () => [...baseSourceAssets, ...toothSliceAssets],
    [baseSourceAssets, toothSliceAssets],
  );

  /**
   * GP tooth-slice stacks are excluded from the unfiltered source list (the
   * response would carry thousands of assets). They are fetched on demand
   * once the user picks a tooth: one small request per selection returns the
   * tens of sequential slices for that tooth.
   */
  const loadToothSlices = useCallback(
    async (toothISO: number | undefined) => {
      if (toothISO === undefined) {
        setToothSliceAssets([]);
        return;
      }
      try {
        const response =
          await api.patientDocument.listPatientDocumentSourceAssets(
            new ListPatientDocumentSourceAssetsReq({
              PatientID: patientID,
              ToothISO: toothISO,
            }),
          );
        const slices = response.Assets.filter(
          (source): source is typeof source & { Asset: Asset } =>
            source.Asset?.Type ===
            AssetType.AssetType_Report_Generated_CBCT_GP_ToothSlice,
        );
        slices.sort(
          (a, b) =>
            (reportImageDetails(a.Asset, formatMessage).order ?? 0) -
            (reportImageDetails(b.Asset, formatMessage).order ?? 0),
        );
        setToothSliceAssets(slices);
        const assets = slices
          .map((source) => source.Asset)
          .filter((asset): asset is Asset => Boolean(asset));
        if (assets.length) {
          dispatch(assetsModel.actions.setMany(assets));
        }
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : String(reason));
      }
    },
    [patientID],
  );

  // Load the per-tooth GP slice stack whenever the user picks a tooth in the
  // picker (and clear it when the filter resets or the study changes).
  useEffect(() => {
    if (!assetPickerOpen) {
      return;
    }
    void loadToothSlices(sourceToothFilter);
  }, [assetPickerOpen, loadToothSlices, sourceToothFilter, sourceReportID]);

  const openAssetPicker = () => {
    setLightboxAsset(null);
    setSourceSearch('');
    setSourceTypeFilter(undefined);
    setSourceToothFilter(undefined);
    setSourceOrientationFilter(undefined);
    setAssetPickerOpen(true);
    window.sessionStorage.setItem(assetPickerStorageKey, 'true');
  };

  const closeAssetPicker = () => {
    setAssetPickerOpen(false);
    window.sessionStorage.removeItem(assetPickerStorageKey);
  };

  useEffect(() => {
    if (assetPickerOpen) {
      void loadSourceAssets();
    }
  }, [assetPickerOpen, loadSourceAssets]);

  useEffect(() => {
    if (!assetPickerOpen) {
      return undefined;
    }

    const focusTimer = window.setTimeout(
      () => assetBrowserRef.current?.focus(),
      50,
    );

    const handlePickerEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      // The zoomed-image popup sits on top of the picker: Esc closes it first.
      if (lightboxAsset) {
        setLightboxAsset(null);
        return;
      }
      setAssetPickerOpen(false);
      window.sessionStorage.removeItem(assetPickerStorageKey);
    };

    window.addEventListener('keydown', handlePickerEscape, true);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', handlePickerEscape, true);
    };
  }, [assetPickerOpen, assetPickerStorageKey, lightboxAsset]);

  const stageSourceAsset = async (source: PatientDocumentSourceAsset) => {
    if (!source.Asset) {
      return;
    }
    setBusy(true);
    setError('');
    let implicitDocument: {
      document: PatientDocument;
      created: boolean;
    } | null = null;
    try {
      implicitDocument = await ensureDocument();
      const document = implicitDocument.document;
      const response = await api.patientDocument.addPatientDocumentImage(
        new AddPatientDocumentImageReq({
          DocumentID: document.ID,
          AssetID: source.Asset.ID,
          Caption: reportImageDescription(source.Asset, formatMessage),
          ExpectedRevision: document.Revision?.Number ?? 0,
        }),
      );
      if (!response.Document) {
        throw new Error(
          formatMessage(patientDocumentMessages.imageInsertFailed),
        );
      }
      setSelected(response.Document);
      const image =
        response.Document.Images[response.Document.Images.length - 1];
      setEditorMutationPending(true);
      insertImageRef.current?.(image.ID, () => setEditorMutationPending(false));
    } catch (reason) {
      await discardImplicitDocument(implicitDocument);
      await load();
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
      setSourceAssets([]);
      setToothSliceAssets([]);
      setSourceReportID('');
      setAssetPickerOpen(false);
      window.sessionStorage.removeItem(assetPickerStorageKey);
    }
  };

  const sourceReportIDs = useMemo(
    () => Array.from(new Set(sourceAssets.map((source) => source.ReportID))),
    [sourceAssets],
  );
  const sourceTypes = useMemo(() => {
    const types = new Map<AssetType, number>();
    sourceAssets
      .filter(
        (source): source is typeof source & { Asset: Asset } =>
          source.ReportID === sourceReportID && Boolean(source.Asset),
      )
      .forEach((source) => {
        const type = source.Asset.Type;
        types.set(type, (types.get(type) ?? 0) + 1);
      });
    return Array.from(types.entries());
  }, [sourceAssets, sourceReportID]);
  const sourceTeeth = useMemo(
    () =>
      Array.from(
        new Set(
          sourceAssets
            .filter(
              (source) =>
                source.ReportID === sourceReportID &&
                source.Asset &&
                (sourceTypeFilter === undefined ||
                  source.Asset.Type === sourceTypeFilter),
            )
            .map((source) =>
              source.Asset
                ? reportImageDetails(source.Asset, formatMessage).tooth
                : undefined,
            )
            .filter((tooth): tooth is number => Boolean(tooth)),
        ),
      ).sort((a, b) => a - b),
    [sourceAssets, sourceReportID, sourceTypeFilter],
  );
  const sourceOrientations = useMemo(
    () =>
      Array.from(
        new Set(
          sourceAssets
            .filter(
              (source) =>
                source.ReportID === sourceReportID &&
                source.Asset &&
                (sourceTypeFilter === undefined ||
                  source.Asset.Type === sourceTypeFilter),
            )
            .map((source) =>
              source.Asset
                ? reportImageDetails(source.Asset, formatMessage).orientation
                : undefined,
            )
            .filter((orientation): orientation is string =>
              Boolean(orientation),
            ),
        ),
      ),
    [sourceAssets, sourceReportID, sourceTypeFilter],
  );
  const studyHasToothSlices = useMemo(
    () =>
      sourceAssets.some(
        (source) =>
          source.ReportID === sourceReportID &&
          source.Asset &&
          isDicomSliceAsset(source.Asset),
      ),
    [sourceAssets, sourceReportID],
  );
  const matchingSourceAssets = useMemo(() => {
    const query = sourceSearch.trim().toLocaleLowerCase();
    // Tooth slices (DICOM-backed) are shown per-tooth: the user picks a tooth
    // first, then the matching slices appear in the picker.
    const studyHasSlices = studyHasToothSlices;
    return sourceAssets.filter((source) => {
      if (source.ReportID !== sourceReportID || !source.Asset) {
        return false;
      }
      if (
        studyHasSlices &&
        sourceToothFilter === undefined &&
        isDicomSliceAsset(source.Asset)
      ) {
        return false;
      }
      if (
        sourceTypeFilter !== undefined &&
        source.Asset.Type !== sourceTypeFilter
      ) {
        return false;
      }
      const details = reportImageDetails(source.Asset, formatMessage);
      if (
        sourceToothFilter !== undefined &&
        details.tooth !== sourceToothFilter
      ) {
        return false;
      }
      if (
        sourceOrientationFilter !== undefined &&
        details.orientation !== sourceOrientationFilter
      ) {
        return false;
      }
      if (!query) {
        return true;
      }
      return reportImageDescription(source.Asset, formatMessage)
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [
    studyHasToothSlices,
    sourceAssets,
    sourceReportID,
    sourceSearch,
    sourceTypeFilter,
    sourceToothFilter,
    sourceOrientationFilter,
  ]);
  const visibleSourceAssets = useMemo(
    () => matchingSourceAssets.slice(0, visibleSourceAssetCount),
    [matchingSourceAssets, visibleSourceAssetCount],
  );
  return (
    <>
      {!documentID && (
        <>
          {error && (
            <WidgetCard className={styles.card} data-testid="patient-documents">
              <div className={styles.error} role="alert">
                {error}
              </div>
            </WidgetCard>
          )}
          {documentsLoading ? (
            // Skeleton tiles shaped like the real document cards — avoids the
            // flash of a different-looking "documents list" card during load.
            <>
              <Skeleton
                testID="patient-documents"
                width="100%"
                height={188}
                borderRadius={16}
              />
              <Skeleton width="100%" height={188} borderRadius={16} />
            </>
          ) : (
            <>
              {documents.map((document) => (
                <PatientDocumentTileCard
                  key={document.ID}
                  document={document}
                  onClick={() => openDocument(document)}
                  onDelete={
                    canEdit ? () => void deleteDocument(document.ID) : undefined
                  }
                />
              ))}
            </>
          )}
        </>
      )}
      {documentID && (
        <section
          className={`${styles.pageContainer} ${styles.pageWorkspace}`}
          data-component="patient-document-page"
          data-testid="patient-document-page"
        >
          <div className={styles.pageHeader}>
            <div className={styles.documentHeader}>
              <div className={styles.documentHeaderActions}>
                {!editing ? (
                  <Button
                    size="small"
                    variant="tertiary"
                    onClick={printDocument}
                    disabled={!selected || documentsLoading}
                  >
                    {formatMessage(patientDocumentMessages.printPdf)}
                  </Button>
                ) : null}
                {editing ? (
                  <>
                    <Button
                      size="small"
                      variant="tertiary"
                      disabled={busy || editorMutationPending}
                      onClick={cancelEditing}
                    >
                      {formatMessage(patientDocumentMessages.cancel)}
                    </Button>
                    <Button
                      size="small"
                      disabled={busy || editorMutationPending}
                      onClick={save}
                    >
                      {formatMessage(patientDocumentMessages.saveToPatient)}
                    </Button>
                  </>
                ) : canEdit ? (
                  <Button
                    size="small"
                    onClick={beginEdit}
                    disabled={!selected || documentsLoading}
                  >
                    {formatMessage(patientDocumentMessages.editDocument)}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}
          {documentsLoading ? (
            // Skeleton shaped and centered like the document surface.
            <Skeleton
              className={styles.documentSkeleton}
              width="min(100%, 1040px)"
              height="100%"
              borderRadius={16}
            />
          ) : (
            (selected || editing) && (
              <div className={styles.workspaceBody}>
                <div className={styles.documentWorkspace}>
                  <div
                    className={styles.documentMain}
                    key={editing ? 'editor' : 'viewer'}
                  >
                    {editing &&
                    documentsLoading &&
                    Boolean(
                      window.sessionStorage.getItem(selectedStorageKey),
                    ) &&
                    !selected ? (
                      <div className={styles.editorLoading} aria-live="polite">
                        {formatMessage(
                          patientDocumentMessages.loadingDocumentImages,
                        )}
                      </div>
                    ) : editing ? (
                      <div
                        className={styles.editorLayout}
                        data-testid="document-editor-surface"
                      >
                        <input
                          ref={fileInput}
                          hidden
                          type="file"
                          accept="image/*"
                          aria-label={formatMessage(
                            patientDocumentMessages.uploadDocumentImage,
                          )}
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                              void stageUpload(file);
                            }
                          }}
                        />
                        <div
                          className={styles.contentEditor}
                          data-testid="patient-document-editor"
                        >
                          <MarkdownWysiwygEditor
                            title={draft.title}
                            onTitleChange={(title) =>
                              updateDraft({ ...draft, title })
                            }
                            value={draft.body}
                            documentID={selected?.ID ?? ''}
                            images={selected?.Images ?? []}
                            resolveAsset={resolveAssetSrc}
                            insertImageRef={insertImageRef}
                            replaceSelectionRef={replaceSelectionRef}
                            insertBelowSelectionRef={insertBelowSelectionRef}
                            onSelectionChange={setSelectedDocumentText}
                            onChange={updateDraftFromEditor}
                            onInsertImage={openAssetPicker}
                            onUploadImage={() => fileInput.current?.click()}
                            onUpdateImage={updateImage}
                          />
                          {assetPickerOpen && (
                            <div
                              ref={assetBrowserRef}
                              className={styles.assetBrowser}
                              role="dialog"
                              aria-label={formatMessage(
                                patientDocumentMessages.chooseReportImage,
                              )}
                              tabIndex={-1}
                              autoFocus
                              onKeyDown={(event) => {
                                if (event.key === 'Escape') {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  closeAssetPicker();
                                }
                              }}
                            >
                              <div className={styles.assetBrowserHeader}>
                                <strong>
                                  {formatMessage(
                                    patientDocumentMessages.insertImage,
                                  )}
                                </strong>
                                <input
                                  type="search"
                                  aria-label={formatMessage(
                                    patientDocumentMessages.searchReportImages,
                                  )}
                                  placeholder={formatMessage(
                                    patientDocumentMessages.searchImagesPlaceholder,
                                  )}
                                  value={sourceSearch}
                                  onChange={(event) => {
                                    setSourceSearch(event.target.value);
                                    setVisibleSourceAssetCount(12);
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={closeAssetPicker}
                                  aria-label={formatMessage(
                                    patientDocumentMessages.closeImagePicker,
                                  )}
                                >
                                  ×
                                </button>
                              </div>
                              {sourceAssets.length > 0 ? (
                                <div className={styles.assetBrowserLayout}>
                                  <div
                                    className={styles.sourceReportTabs}
                                    aria-label={formatMessage(
                                      patientDocumentMessages.chooseStudy,
                                    )}
                                  >
                                    <span className={styles.assetSectionLabel}>
                                      {formatMessage(
                                        patientDocumentMessages.studies,
                                      )}
                                    </span>
                                    {sourceReportIDs.map((reportID, index) => (
                                      <button
                                        type="button"
                                        key={reportID}
                                        className={
                                          reportID === sourceReportID
                                            ? styles.activeSourceReport
                                            : ''
                                        }
                                        onClick={() => {
                                          setSourceReportID(reportID);
                                          setSourceTypeFilter(undefined);
                                          setSourceToothFilter(undefined);
                                          setSourceOrientationFilter(undefined);
                                          setSourceSearch('');
                                          setVisibleSourceAssetCount(12);
                                        }}
                                      >
                                        <span>
                                          {formatMessage(
                                            patientDocumentMessages.studyNumber,
                                            { number: index + 1 },
                                          )}
                                        </span>
                                        <small>
                                          {formatMessage(
                                            patientDocumentMessages.imageCount,
                                            {
                                              count: sourceAssets.filter(
                                                (source) =>
                                                  source.ReportID === reportID,
                                              ).length,
                                            },
                                          )}
                                        </small>
                                      </button>
                                    ))}
                                    <div className={styles.seriesList}>
                                      <button
                                        type="button"
                                        className={
                                          sourceTypeFilter === undefined
                                            ? styles.activeSeries
                                            : ''
                                        }
                                        onClick={() => {
                                          setSourceTypeFilter(undefined);
                                          setSourceToothFilter(undefined);
                                          setSourceOrientationFilter(undefined);
                                        }}
                                      >
                                        {formatMessage(
                                          patientDocumentMessages.allAvailable,
                                        )}{' '}
                                        <span>
                                          {
                                            sourceAssets.filter(
                                              (source) =>
                                                source.ReportID ===
                                                  sourceReportID &&
                                                source.Asset,
                                            ).length
                                          }
                                        </span>
                                      </button>
                                      {sourceTypes.map(([type, count]) => (
                                        <button
                                          type="button"
                                          key={type}
                                          className={
                                            sourceTypeFilter === type
                                              ? styles.activeSeries
                                              : ''
                                          }
                                          onClick={() => {
                                            setSourceTypeFilter(type);
                                            setSourceToothFilter(undefined);
                                            setSourceOrientationFilter(
                                              undefined,
                                            );
                                            setVisibleSourceAssetCount(12);
                                          }}
                                        >
                                          {reportImageTypeLabel(
                                            type,
                                            formatMessage,
                                          )}
                                          <span>{count}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  <div className={styles.assetBrowserContent}>
                                    <div className={styles.assetBreadcrumb}>
                                      <span>
                                        {formatMessage(
                                          patientDocumentMessages.studyNumber,
                                          {
                                            number: Math.max(
                                              1,
                                              sourceReportIDs.indexOf(
                                                sourceReportID,
                                              ) + 1,
                                            ),
                                          },
                                        )}
                                      </span>
                                      <span>/</span>
                                      <strong>
                                        {sourceTypeFilter === undefined
                                          ? formatMessage(
                                              patientDocumentMessages.allAvailableImages,
                                            )
                                          : reportImageTypeLabel(
                                              sourceTypeFilter,
                                              formatMessage,
                                            )}
                                      </strong>
                                    </div>
                                    {(sourceTeeth.length > 0 ||
                                      sourceOrientations.length > 0) && (
                                      <div
                                        className={styles.toothFilters}
                                        aria-label={formatMessage(
                                          patientDocumentMessages.filterReportImages,
                                        )}
                                      >
                                        {sourceTeeth.length > 0 && (
                                          <div
                                            className={styles.toothButtons}
                                            aria-label={formatMessage(
                                              patientDocumentMessages.filterByTooth,
                                            )}
                                          >
                                            {sourceTeeth.map((tooth) => (
                                              <button
                                                type="button"
                                                key={tooth}
                                                className={
                                                  sourceToothFilter === tooth
                                                    ? styles.toothButtonActive
                                                    : styles.toothButton
                                                }
                                                aria-pressed={
                                                  sourceToothFilter === tooth
                                                }
                                                onClick={() => {
                                                  setSourceToothFilter(
                                                    sourceToothFilter === tooth
                                                      ? undefined
                                                      : tooth,
                                                  );
                                                }}
                                              >
                                                {tooth}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                        {sourceOrientations.length > 0 && (
                                          <label>
                                            {formatMessage(
                                              patientDocumentMessages.orientation,
                                            )}
                                            <select
                                              aria-label={formatMessage(
                                                patientDocumentMessages.filterByOrientation,
                                              )}
                                              value={
                                                sourceOrientationFilter ?? ''
                                              }
                                              onChange={(event) => {
                                                setSourceOrientationFilter(
                                                  event.target.value ||
                                                    undefined,
                                                );
                                              }}
                                            >
                                              <option value="">
                                                {formatMessage(
                                                  patientDocumentMessages.allOrientations,
                                                )}
                                              </option>
                                              {sourceOrientations.map(
                                                (orientation) => (
                                                  <option
                                                    key={orientation}
                                                    value={orientation}
                                                  >
                                                    {orientation}
                                                  </option>
                                                ),
                                              )}
                                            </select>
                                          </label>
                                        )}
                                      </div>
                                    )}
                                    <div className={styles.reportFigures}>
                                      <strong>
                                        {formatMessage(
                                          patientDocumentMessages.availableImages,
                                        )}
                                      </strong>
                                      {studyHasToothSlices &&
                                      sourceToothFilter === undefined ? (
                                        <span className={styles.toothHint}>
                                          {formatMessage(
                                            patientDocumentMessages.selectTooth,
                                          )}
                                        </span>
                                      ) : null}
                                      <div
                                        aria-label={formatMessage(
                                          patientDocumentMessages.imagesFromReports,
                                        )}
                                      >
                                        {visibleSourceAssets.map((source) =>
                                          source.Asset ? (
                                            <button
                                              type="button"
                                              key={source.Asset.ID}
                                              onClick={() =>
                                                setLightboxAsset(source)
                                              }
                                            >
                                              {failedSourceAssetIDs.has(
                                                source.Asset.ID,
                                              ) ? (
                                                <span
                                                  className={styles.brokenAsset}
                                                  aria-label={formatMessage(
                                                    patientDocumentMessages.imageUnavailable,
                                                  )}
                                                >
                                                  {formatMessage(
                                                    patientDocumentMessages.imageUnavailable,
                                                  )}
                                                </span>
                                              ) : (
                                                <img
                                                  src={getImageSrc(
                                                    source.Asset.ID,
                                                    isDicomSliceAsset(
                                                      source.Asset,
                                                    )
                                                      ? 'preview'
                                                      : 'thumbnail',
                                                  )}
                                                  alt={reportImageDescription(
                                                    source.Asset,
                                                    formatMessage,
                                                  )}
                                                  loading="lazy"
                                                  onError={(event) => {
                                                    const image =
                                                      event.currentTarget;
                                                    if (
                                                      image.src.includes(
                                                        '/thumbnail/',
                                                      )
                                                    ) {
                                                      image.src =
                                                        image.src.replace(
                                                          '/thumbnail/',
                                                          '/original/',
                                                        );
                                                    } else {
                                                      setFailedSourceAssetIDs(
                                                        (failed) =>
                                                          new Set(failed).add(
                                                            source.Asset!.ID,
                                                          ),
                                                      );
                                                    }
                                                  }}
                                                />
                                              )}
                                              <span>
                                                {reportImageDescription(
                                                  source.Asset,
                                                  formatMessage,
                                                )}
                                              </span>
                                            </button>
                                          ) : null,
                                        )}
                                      </div>
                                      {visibleSourceAssets.length <
                                      matchingSourceAssets.length ? (
                                        <button
                                          type="button"
                                          className={styles.loadMoreImages}
                                          onClick={() =>
                                            setVisibleSourceAssetCount(
                                              (count) => count + 12,
                                            )
                                          }
                                        >
                                          {formatMessage(
                                            patientDocumentMessages.loadMoreImages,
                                          )}
                                        </button>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              ) : sourceAssetsLoading ? (
                                <div className={styles.assetEmpty}>
                                  {formatMessage(
                                    patientDocumentMessages.loadingImaging,
                                  )}
                                </div>
                              ) : (
                                <div className={styles.assetEmpty}>
                                  {formatMessage(
                                    patientDocumentMessages.noCompatibleImages,
                                  )}
                                </div>
                              )}
                              <div className={styles.assetBrowserFooter}>
                                <span>
                                  {formatMessage(
                                    patientDocumentMessages.imagePickerHint,
                                  )}
                                </span>
                              </div>
                              {lightboxAsset?.Asset ? (
                                <div
                                  className={styles.lightbox}
                                  role="dialog"
                                  aria-label={formatMessage(
                                    patientDocumentMessages.imagePreview,
                                  )}
                                  onClick={() => setLightboxAsset(null)}
                                >
                                  <div
                                    className={styles.lightboxBody}
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      className={styles.lightboxClose}
                                      aria-label={formatMessage(
                                        patientDocumentMessages.closePreview,
                                      )}
                                      onClick={() => setLightboxAsset(null)}
                                    >
                                      ×
                                    </button>
                                    <div className={styles.lightboxImage}>
                                      {isDicomSliceAsset(
                                        lightboxAsset.Asset,
                                      ) ? (
                                        <DocumentImageDisplay
                                          assetID={lightboxAsset.Asset.ID}
                                          documentID={
                                            'picker-' + sourceReportID
                                          }
                                          renderFallback={() => (
                                            <span
                                              className={styles.brokenAsset}
                                              aria-label={formatMessage(
                                                patientDocumentMessages.renderingSlice,
                                              )}
                                            >
                                              {formatMessage(
                                                patientDocumentMessages.renderingSlice,
                                              )}
                                            </span>
                                          )}
                                        />
                                      ) : (
                                        <img
                                          src={getImageSrc(
                                            lightboxAsset.Asset.ID,
                                            'preview',
                                          )}
                                          alt={reportImageDescription(
                                            lightboxAsset.Asset,
                                            formatMessage,
                                          )}
                                          onError={(event) => {
                                            // Derivatives are missing for some assets;
                                            // fall back to the original only then.
                                            const image = event.currentTarget;
                                            if (
                                              !image.src.includes('/original/')
                                            ) {
                                              image.src = image.src.replace(
                                                '/preview/',
                                                '/original/',
                                              );
                                            }
                                          }}
                                        />
                                      )}
                                    </div>
                                    <div className={styles.lightbar}>
                                      <span>
                                        {reportImageDescription(
                                          lightboxAsset.Asset,
                                          formatMessage,
                                        )}
                                      </span>
                                      <Button
                                        disabled={busy}
                                        onClick={() => {
                                          setLightboxAsset(null);
                                          void stageSourceAsset(lightboxAsset);
                                        }}
                                      >
                                        {formatMessage(
                                          patientDocumentMessages.insertImage,
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : selected ? (
                      <article
                        className={styles.preview}
                        data-testid="patient-document-preview"
                      >
                        <h1>{selected.Title}</h1>
                        <MarkdownPreview
                          markdown={withoutRedundantTitle(
                            selected.Body,
                            selected.Title,
                          )}
                          resolveAsset={(source) => {
                            const serial = source.startsWith('image:')
                              ? Number(source.slice('image:'.length))
                              : NaN;
                            const image =
                              Number.isSafeInteger(serial) && serial > 0
                                ? selected.Images.find(
                                    (item) => item.ID === serial,
                                  )
                                : undefined;
                            return image
                              ? resolveAssetSrc(image.AssetID)
                              : undefined;
                          }}
                          captionFor={(source) => {
                            const serial = source.startsWith('image:')
                              ? Number(source.slice('image:'.length))
                              : NaN;
                            const image =
                              Number.isSafeInteger(serial) && serial > 0
                                ? selected.Images.find(
                                    (item) => item.ID === serial,
                                  )
                                : undefined;
                            return image?.Caption || undefined;
                          }}
                          renderImage={({ destination, alt, style }) => {
                            const serial = destination.startsWith('image:')
                              ? Number(destination.slice('image:'.length))
                              : NaN;
                            const image =
                              Number.isSafeInteger(serial) && serial > 0
                                ? selected.Images.find(
                                    (item) => item.ID === serial,
                                  )
                                : undefined;
                            if (!image) {
                              return undefined;
                            }
                            return (
                              <DocumentImageDisplay
                                assetID={image.AssetID}
                                documentID={selected.ID}
                                renderFallback={() => (
                                  <PreviewImage
                                    source={resolveAssetSrc(image.AssetID)}
                                    alt={alt}
                                    style={style}
                                  />
                                )}
                              />
                            );
                          }}
                          styleFor={(source) => {
                            const serial = source.startsWith('image:')
                              ? Number(source.slice('image:'.length))
                              : NaN;
                            const image =
                              Number.isSafeInteger(serial) && serial > 0
                                ? selected.Images.find(
                                    (item) => item.ID === serial,
                                  )
                                : undefined;
                            if (!image) {
                              return undefined;
                            }
                            return {
                              transform: imageViewTransform(image.ViewOptions),
                              filter: imageViewFilter(image.ViewOptions),
                            };
                          }}
                        />
                      </article>
                    ) : null}
                  </div>
                  <AgentChatHost
                    patientID={patientID}
                    documentMode
                    documentID={selected?.ID || ''}
                    documentName={selected?.Title || undefined}
                    onDocumentChanged={() => void load()}
                    onOpenDocument={(documentID) => {
                      void api.patientDocument
                        .listPatientDocuments(
                          new ListPatientDocumentsReq({
                            PatientID: patientID,
                          }),
                        )
                        .then((response) => {
                          setDocuments(response.Documents);
                          const document = response.Documents.find(
                            (item) => item.ID === documentID,
                          );
                          if (document) {
                            openDocument(document);
                          }
                        })
                        .catch((reason) =>
                          setError(
                            reason instanceof Error
                              ? reason.message
                              : String(reason),
                          ),
                        );
                    }}
                    selectedDocumentText={selectedDocumentText}
                    onReplaceDocumentSelection={(text) => {
                      const applied =
                        replaceSelectionRef.current?.(text) ?? false;
                      if (!applied) {
                        setError(
                          formatMessage(
                            patientDocumentMessages.selectTextRewrite,
                          ),
                        );
                      }
                      return applied;
                    }}
                    onInsertBelowDocumentSelection={(text) => {
                      const applied =
                        insertBelowSelectionRef.current?.(text) ?? false;
                      if (!applied) {
                        setError(
                          formatMessage(
                            patientDocumentMessages.selectTextInsert,
                          ),
                        );
                      }
                      return applied;
                    }}
                  />
                </div>
              </div>
            )
          )}
        </section>
      )}
    </>
  );
};
